export interface ImageFilterSettings {
  brightness: number;
  contrast: number;
  saturate: number;
  hueRotate: number;
  sepia: number;
  grayscale: number;
  vignette: number;
  temperature: number;
  grain: number;
  fade: number;
}

export interface ImageFilterPreset {
  id: string;
  label: string;
  settings: ImageFilterSettings;
}

export const DEFAULT_IMAGE_FILTER_SETTINGS: ImageFilterSettings = {
  brightness: 1,
  contrast: 1,
  saturate: 1,
  hueRotate: 0,
  sepia: 0,
  grayscale: 0,
  vignette: 0,
  temperature: 0,
  grain: 0,
  fade: 0,
};

export const IMAGE_FILTER_CONTROL_BOUNDS = {
  brightness: { min: 0, max: 2, step: 0.05 },
  contrast: { min: 0, max: 2, step: 0.05 },
  saturate: { min: 0, max: 3, step: 0.05 },
  hueRotate: { min: 0, max: 360, step: 1 },
  sepia: { min: 0, max: 1, step: 0.05 },
  grayscale: { min: 0, max: 1, step: 0.05 },
  vignette: { min: 0, max: 1, step: 0.05 },
  temperature: { min: -1, max: 1, step: 0.05 },
  grain: { min: 0, max: 1, step: 0.05 },
  fade: { min: 0, max: 1, step: 0.05 },
} as const;

export const IMAGE_FILTER_PRESETS: ImageFilterPreset[] = [
  {
    id: 'original',
    label: 'Original',
    settings: DEFAULT_IMAGE_FILTER_SETTINGS,
  },
  {
    id: 'vivid',
    label: 'Vivid',
    settings: {
      brightness: 1.05,
      contrast: 1.15,
      saturate: 1.4,
      hueRotate: 0,
      sepia: 0,
      grayscale: 0,
      vignette: 0,
      temperature: 0,
      grain: 0,
      fade: 0,
    },
  },
  {
    id: 'warm-sunset',
    label: 'Warm Sunset',
    settings: {
      brightness: 1.1,
      contrast: 1.05,
      saturate: 1.2,
      hueRotate: 10,
      sepia: 0.15,
      grayscale: 0,
      vignette: 0,
      temperature: 0.4,
      grain: 0,
      fade: 0,
    },
  },
  {
    id: 'cool-night',
    label: 'Cool Night',
    settings: {
      brightness: 0.95,
      contrast: 1.2,
      saturate: 1.1,
      hueRotate: 340,
      sepia: 0,
      grayscale: 0,
      vignette: 0.3,
      temperature: -0.4,
      grain: 0,
      fade: 0,
    },
  },
  {
    id: 'monochrome',
    label: 'Monochrome',
    settings: {
      brightness: 1,
      contrast: 1.3,
      saturate: 1,
      hueRotate: 0,
      sepia: 0,
      grayscale: 1,
      vignette: 0,
      temperature: 0,
      grain: 0,
      fade: 0,
    },
  },
  {
    id: 'retro',
    label: 'Retro',
    settings: {
      brightness: 1.05,
      contrast: 0.85,
      saturate: 0.7,
      hueRotate: 0,
      sepia: 0.3,
      grayscale: 0,
      vignette: 0.2,
      temperature: 0.2,
      grain: 0.1,
      fade: 0,
    },
  },
  {
    id: 'high-contrast',
    label: 'High Contrast',
    settings: {
      brightness: 1,
      contrast: 1.5,
      saturate: 1.2,
      hueRotate: 0,
      sepia: 0,
      grayscale: 0,
      vignette: 0,
      temperature: 0,
      grain: 0,
      fade: 0,
    },
  },
  {
    id: 'soft-glow',
    label: 'Soft Glow',
    settings: {
      brightness: 1.15,
      contrast: 0.9,
      saturate: 1.1,
      hueRotate: 0,
      sepia: 0.05,
      grayscale: 0,
      vignette: 0.2,
      temperature: 0,
      grain: 0,
      fade: 0,
    },
  },
  {
    id: 'cyberpunk',
    label: 'Cyberpunk',
    settings: {
      brightness: 1.05,
      contrast: 1.3,
      saturate: 1.8,
      hueRotate: 280,
      sepia: 0,
      grayscale: 0,
      vignette: 0,
      temperature: -0.2,
      grain: 0.15,
      fade: 0,
    },
  },
  {
    id: 'dreamy',
    label: 'Dreamy',
    settings: {
      brightness: 1.2,
      contrast: 0.85,
      saturate: 0.8,
      hueRotate: 330,
      sepia: 0.1,
      grayscale: 0,
      vignette: 0.15,
      temperature: 0,
      grain: 0,
      fade: 0.2,
    },
  },
  {
    id: 'faded-film',
    label: 'Faded Film',
    settings: {
      brightness: 1.1,
      contrast: 0.9,
      saturate: 0.6,
      hueRotate: 5,
      sepia: 0.2,
      grayscale: 0.1,
      vignette: 0,
      temperature: 0.1,
      grain: 0.1,
      fade: 0.25,
    },
  },
  {
    id: 'neon',
    label: 'Neon',
    settings: {
      brightness: 1.1,
      contrast: 1.4,
      saturate: 2.2,
      hueRotate: 180,
      sepia: 0,
      grayscale: 0,
      vignette: 0,
      temperature: -0.15,
      grain: 0,
      fade: 0,
    },
  },
  {
    id: 'autumn',
    label: 'Autumn',
    settings: {
      brightness: 1.05,
      contrast: 1.1,
      saturate: 1.3,
      hueRotate: 20,
      sepia: 0.25,
      grayscale: 0,
      vignette: 0.1,
      temperature: 0.3,
      grain: 0,
      fade: 0,
    },
  },
  {
    id: 'noir',
    label: 'Noir',
    settings: {
      brightness: 0.9,
      contrast: 1.4,
      saturate: 0.3,
      hueRotate: 0,
      sepia: 0.1,
      grayscale: 0.6,
      vignette: 0.4,
      temperature: 0,
      grain: 0.1,
      fade: 0,
    },
  },
];

function clampValue(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function clampImageFilterSettings(
  settings: Partial<ImageFilterSettings> | undefined
): ImageFilterSettings {
  return {
    brightness: clampValue(
      settings?.brightness ?? DEFAULT_IMAGE_FILTER_SETTINGS.brightness,
      IMAGE_FILTER_CONTROL_BOUNDS.brightness.min,
      IMAGE_FILTER_CONTROL_BOUNDS.brightness.max
    ),
    contrast: clampValue(
      settings?.contrast ?? DEFAULT_IMAGE_FILTER_SETTINGS.contrast,
      IMAGE_FILTER_CONTROL_BOUNDS.contrast.min,
      IMAGE_FILTER_CONTROL_BOUNDS.contrast.max
    ),
    saturate: clampValue(
      settings?.saturate ?? DEFAULT_IMAGE_FILTER_SETTINGS.saturate,
      IMAGE_FILTER_CONTROL_BOUNDS.saturate.min,
      IMAGE_FILTER_CONTROL_BOUNDS.saturate.max
    ),
    hueRotate: clampValue(
      settings?.hueRotate ?? DEFAULT_IMAGE_FILTER_SETTINGS.hueRotate,
      IMAGE_FILTER_CONTROL_BOUNDS.hueRotate.min,
      IMAGE_FILTER_CONTROL_BOUNDS.hueRotate.max
    ),
    sepia: clampValue(
      settings?.sepia ?? DEFAULT_IMAGE_FILTER_SETTINGS.sepia,
      IMAGE_FILTER_CONTROL_BOUNDS.sepia.min,
      IMAGE_FILTER_CONTROL_BOUNDS.sepia.max
    ),
    grayscale: clampValue(
      settings?.grayscale ?? DEFAULT_IMAGE_FILTER_SETTINGS.grayscale,
      IMAGE_FILTER_CONTROL_BOUNDS.grayscale.min,
      IMAGE_FILTER_CONTROL_BOUNDS.grayscale.max
    ),
    vignette: clampValue(
      settings?.vignette ?? DEFAULT_IMAGE_FILTER_SETTINGS.vignette,
      IMAGE_FILTER_CONTROL_BOUNDS.vignette.min,
      IMAGE_FILTER_CONTROL_BOUNDS.vignette.max
    ),
    temperature: clampValue(
      settings?.temperature ?? DEFAULT_IMAGE_FILTER_SETTINGS.temperature,
      IMAGE_FILTER_CONTROL_BOUNDS.temperature.min,
      IMAGE_FILTER_CONTROL_BOUNDS.temperature.max
    ),
    grain: clampValue(
      settings?.grain ?? DEFAULT_IMAGE_FILTER_SETTINGS.grain,
      IMAGE_FILTER_CONTROL_BOUNDS.grain.min,
      IMAGE_FILTER_CONTROL_BOUNDS.grain.max
    ),
    fade: clampValue(
      settings?.fade ?? DEFAULT_IMAGE_FILTER_SETTINGS.fade,
      IMAGE_FILTER_CONTROL_BOUNDS.fade.min,
      IMAGE_FILTER_CONTROL_BOUNDS.fade.max
    ),
  };
}

export function buildImageCssFilter(settings: Partial<ImageFilterSettings> | undefined): string {
  const normalized = clampImageFilterSettings(settings);
  return [
    `brightness(${normalized.brightness})`,
    `contrast(${normalized.contrast})`,
    `saturate(${normalized.saturate})`,
    `hue-rotate(${normalized.hueRotate}deg)`,
    `sepia(${normalized.sepia})`,
    `grayscale(${normalized.grayscale})`,
  ].join(' ');
}

export function isDefaultImageFilterSettings(
  settings: Partial<ImageFilterSettings> | undefined
): boolean {
  const normalized = clampImageFilterSettings(settings);
  return Object.entries(DEFAULT_IMAGE_FILTER_SETTINGS).every(
    ([key, value]) => normalized[key as keyof ImageFilterSettings] === value
  );
}

export function buildFilteredFilename(
  originalFilename: string | null | undefined,
  suffix: string = 'filtered'
): string {
  const fallbackName =
    originalFilename && originalFilename.trim().length > 0 ? originalFilename : 'photo.jpg';
  const extensionMatch = fallbackName.match(/(\.[^.]+)$/);
  const extension = extensionMatch?.[1] ?? '.jpg';
  const baseName = extensionMatch ? fallbackName.slice(0, -extension.length) : fallbackName;
  return `${baseName}_${suffix}${extension}`;
}
